import {
  Box,
  capitalize,
  Collapse,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { TableCheckBox } from "./TablecheckBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { DamsAppAsset } from "../../assets/registry";
import { DamsColors } from "../styles/DamsColors";
import { CollapseTableLabelItem } from "./infoLabel";
import { TableActionIcon } from "./TableActionIcon";

export interface TRowItem {
  [key: string]: any;
}

function DamsTableRow({
  TRowItem,
  maxRowCells,
  isSelected,
  hasCheckBox,
  TableKeys,
  HiddenKeys,
  HiddenLabels,
  onItemDelete,
  onItemEdit,
  toggleSelection,
  extraRowComponent,
}: Readonly<{
  TRowItem?: TRowItem;
  isSelected?: boolean;
  maxRowCells?: number;
  hasCheckBox?: boolean;
  TableKeys?: string[];
  HiddenKeys?: string[];
  HiddenLabels?: string[];
  onItemEdit?: (TRowItem: TRowItem) => void;
  onItemDelete?: (TRowItem: TRowItem) => void;
  toggleSelection?: (TRowItem: TRowItem) => void;
  extraRowComponent?: React.ElementType<any>;
}>) {
  const [open, setOpen] = React.useState(false);

  const handleItemClick = () => {
    if (TRowItem !== undefined) {
      toggleSelection?.(TRowItem);
    }
  };

  const IconComponent = open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  const iconStyles = {
    ...styles.iconStyle,
    ...(isSelected && styles.iconStyleActive),
  };

  const maxCells = maxRowCells ?? 10;
  const showExtraItems = HiddenKeys?.length! > 0;

  const transformedValue = (value: string | number | undefined | null) => {
    if (typeof value === "string") {
      return capitalize(value);
    }
    return value ?? "-";
  };
  return (
    <React.Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        {hasCheckBox && (
          <TableCell padding="checkbox" align="center">
            <TableCheckBox isChecked={isSelected} onClick={handleItemClick} />
          </TableCell>
        )}

        {/* Dynamically map over TableKeys to render row data */}
        {TableKeys?.slice(0, maxCells).map((key, index) => (
          <TableCell
            key={`t-row-${index} ${key}`}
            component={index === 0 ? "th" : "td"}
            scope={index === 0 ? "row" : undefined}
            align="left"
          >
            <Typography
              fontFamily={"Inter"}
              color={
                isSelected ? DamsColors.Text.Highlight : DamsColors.Text.Black2
              }
              fontWeight={400}
              fontSize={14}
            >
              {TRowItem
                ? TRowItem[key] === true
                  ? "True"
                  : TRowItem[key] === false
                  ? "False"
                  : TRowItem[key] && transformedValue(TRowItem[key])
                : "-"}
            </Typography>
          </TableCell>
        ))}

        <TableCell align="right" sx={{minWidth:70}}>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            {(showExtraItems || onItemEdit || onItemDelete) && (
              <Stack direction={"row"}>
                {showExtraItems && (
                  <TableActionIcon
                    tip="more info"
                    onIconClick={() => setOpen(!open)}
                    ariaLabel="expand row"
                  >
                    <IconComponent sx={iconStyles} />
                  </TableActionIcon>
                )}

                {onItemEdit && (
                  <TableActionIcon
                    onIconClick={() => onItemEdit(TRowItem!)}
                    tip="Edit"
                    ariaLabel="edit row Item"
                    IconSrc={
                      isSelected
                        ? DamsAppAsset.icons.edit_green
                        : DamsAppAsset.icons.edit_item
                    }
                  />
                )}

                {onItemDelete && (
                  <TableActionIcon
                    tip="Delete"
                    onIconClick={() => onItemDelete(TRowItem!)}
                    ariaLabel="delete row item"
                    IconSrc={
                      isSelected
                        ? DamsAppAsset.icons.delete_green
                        : DamsAppAsset.icons.delete_item
                    }
                  />
                )}
              </Stack>
            )}

            {extraRowComponent &&
              TRowItem &&
              React.createElement(extraRowComponent, {
                item: TRowItem,
              })}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow sx={{ backgroundColor: DamsColors.Background.TableHeader }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              width={"100%"}
              display={"flex"}
              flexWrap={"wrap"}
              columnGap={6}
              rowGap={2}
              py={2}
              px={6}
            >
              {/* Dynamically map over HiddenKeys to render hidden row data */}
              {HiddenKeys?.map((key, index) => (
                <CollapseTableLabelItem
                  key={`collapse-item-${index}`}
                  title={HiddenLabels ? HiddenLabels[index] : key}
                  value={TRowItem ? TRowItem[key] : "-"}
                  selected={isSelected}
                />
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const styles = {
  iconStyle: {
    height: "20px",
    width: "20px",
    color: DamsColors.Text.LabelTextLight,
  },

  iconStyleActive: {
    color: DamsColors.Text.Highlight,
  },
};

export { DamsTableRow };
